import { filter } from 'lodash';
import EnrouteApiClient from '../../shared/services/enroute-api-client';
import EnrouteApiV2Client from '../../shared/services/enroute-api-v2-client';
import {
  JobPayload,
  Job,
  ReassignJobPayload,
  Operation,
  JobPhase,
  LoadJobsParams,
  SearchJobsParams,
} from '../../shared/models/jobs';
import { mapAxiosResponse } from '../../shared/utils/api-utils';
import { ServerError, ServerErrorMessages } from '../../shared/utils/server-error-utils';

export default class JobsService {

  public static async createJob(orgID: number, body: JobPayload): Promise<Job | Error> {
    const due_time = new Date(body.appointment.to || body.appointment.from);

    let destination;
    if (body.destination.address && !body.destination.showCoordinates) {
      const { address } = body.destination;

      destination = {
        name: address,
        address,
      };
    } else {
      destination = {
        name: body.destination.name,
        lat: body.destination.lat,
        lng: body.destination.lng,
      };
    }

    const metadata = [
      {
        pid: 0,
        n: 'application',
        v: 'glympsepro.v1',
      },
      {
        pid: 0,
        n: 'era_recipient_name',
        v: body.customer.name,
      },
      {
        pid: 0,
        n: 'era_phone',
        v: body.customer.phones[0],
      },
      {
        pid: 0,
        n: 'appt_from',
        v: (new Date(body.appointment.from)).getTime(),
      },
      {
        pid: 0,
        n: 'appt_to',
        v: due_time.getTime(),
      },
      {
        pid: 0,
        n: 'appointment_id',
        v: body.appointment.id,
      },
    ];

    return EnrouteApiClient.post(`/org/${ orgID }/create_task?agent_id=${ body.technician }`, {
      destination,
      due_time: due_time.getTime(),
      description: body.description,
      foreign_id: body.appointment.id,
      recipients: [
        ...([...body.customer.phones, ...body.customer.emails].filter(item => item).map(address => ({
          name: body.customer.name,
          address,
        })))
      ],
      metadata: filter(metadata, (value) => value.v),
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async updateJob(jobID: number, body: JobPayload) {
    const apptTo =  body.appointment.from > body.appointment.to ? body.appointment.from : body.appointment.to;
    const dueTime = new Date(apptTo);

    let destination;
    if (body.destination.address && !body.destination.showCoordinates) {
      const { address } = body.destination;

      destination = {
        name: address,
        address,
      };
    } else {
      destination = {
        name: body.destination.name,
        lat: body.destination.lat,
        lng: body.destination.lng,
      };
    }

    const metadata = [
      {
        pid: 0,
        n: 'era_recipient_name',
        v: body.customer.name,
      },
      {
        pid: 0,
        n: 'era_phone',
        v: body.customer.phones[0],
      },
      {
        pid: 0,
        n: 'appt_from',
        v: (new Date(body.appointment.from)).getTime(),
      },
      {
        pid: 0,
        n: 'appt_to',
        v: (new Date(apptTo)).getTime(),
      },
      {
        pid: 0,
        n: 'appointment_id',
        v: body.appointment.id,
      }
    ];
    
    return EnrouteApiClient.post(`/task/${ jobID }/update`, {
      destination,
      due_time: dueTime.getTime(),
      description: body.description,
      foreign_id: body.appointment.id,
      recipients: [
        ...([...body.customer.phones, ...body.customer.emails].map(address => ({
          name: body.customer.name,
          address,
        })))
      ],
      metadata: filter(metadata, (value) => value.v),
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async updateStartedJob(jobID: number, body: JobPayload) {
    const apptTo =  body.appointment.from > body.appointment.to ? body.appointment.from : body.appointment.to;
    const dueTime = new Date(apptTo);
    let destination;
    if (body.destination.address && !body.destination.showCoordinates) {
      const { address } = body.destination;

      destination = {
        name: address,
        address,
      };
    } else {
      destination = {
        name: body.destination.name,
        lat: body.destination.lat,
        lng: body.destination.lng,
      };
    }

    const metadata = [
      {
        pid: 0,
        n: 'appt_from',
        v: (new Date(body.appointment.from)).getTime(),
      },
      {
        pid: 0,
        n: 'appt_to',
        v: (new Date(apptTo)).getTime(),
      },
      {
        pid: 0,
        n: 'appointment_id',
        v: body.appointment.id,
      }
    ];
    try {
      if (body.recipients) {
        const update = body.recipients?.update?.filter(recipient => recipient);
        const add = body.recipients?.add?.filter(recipient => recipient);
        // eslint-disable-next-line no-plusplus
        for(let index = 0; index < update.length; index++) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await JobsService.updateRecipient(jobID, update[index]);
          } catch (e) {
            // continue with next recipient even if one fails
          }
        }
        // eslint-disable-next-line no-plusplus
        for(let index = 0; index < add.length; index++) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await JobsService.addRecipient(jobID, add[index]);
          } catch (e) {
            // continue with next recipient even if one fails
          }
        }
      }
    } catch (e) {
      // do nothing
    }
    return EnrouteApiClient.post(`/task/${ jobID }/update_started`, {
      destination,
      description: body.description,
      due_time: dueTime.getTime(),
      foreign_id: body.appointment.id,
      metadata: filter(metadata, (value) => value.v),
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async getJob(id: number): Promise<Job | Error> {
    return EnrouteApiClient.get(`/task/${ id }`, {
      params: {
        operations: true,
      },
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async getJobs(orgID: number, params: LoadJobsParams): Promise<Job[] | Error> {
    return EnrouteApiClient.get(`/org/${ orgID }/tasks`, {
      params: {
        operations: true,
        sort_by: 'order',
        sort_mode: 'desc',
        feedback: true,
        aggregations: true,
        ...params
      },
    })
      .then(resp => mapAxiosResponse<Job[]>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async searchJobs(orgID: number, params: SearchJobsParams): Promise<Job[] | Error> {
    return EnrouteApiV2Client.post(`org/${ orgID }/search/tasks`, {
      ...params
    })
      .then(resp => {
        if (!resp.data) {
          throw new Error('error');
        }
        return resp.data;
        // return mapAxiosResponse<Job[]>(resp)
      })
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async start(id: number): Promise<Operation | Error> {
    return EnrouteApiClient.post(`/task/${ id }/start`)
      .then(resp => mapAxiosResponse<Operation & {org_id: number}>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async complete(id: number) {
    return EnrouteApiClient.post(`/task/${ id }/complete`)
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async updateJobPhase(id: number, phase: JobPhase) {
    return EnrouteApiClient.get(`/task/${ id }/phase_changed`, {
      params: {
        notify: true,
        phase,
      },
    })
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async remove(id: number): Promise<number | Error> {
    return EnrouteApiClient.post(`/task/${ id }/remove`)
      .then(resp => mapAxiosResponse<number>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async cancel(id: number): Promise<number | Error> {
    return EnrouteApiClient.post(`/task/${ id }/phase_changed`, null, {
      params: {
        phase: 'cancelled',
        notify: true,
      },
    })
      .then(resp => mapAxiosResponse<number>(resp))
      .then(() => id)
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async reassign(jobID: number, body: ReassignJobPayload): Promise<Job | Error> {
    return EnrouteApiClient.post(`/task/${ jobID }/assign`, null, {
      params: {
        agent_id: body.technician,
      },
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async updateRecipient(jobID: number, address: string): Promise<Job | Error> {
    return EnrouteApiClient.post(`/task/${ jobID }/update_recipient`, {
      notify: false
    }, {
      params: {
        address,
      },
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async addRecipient(jobID: number, address: string): Promise<Job | Error> {
    return EnrouteApiClient.post(`/task/${ jobID }/add_recipient`, {
      address,
      notify: true
    }, {
      params: {
        address,
      },
    })
      .then(resp => mapAxiosResponse<Job>(resp))
      .catch(error => {
        let errorMessage = ServerErrorMessages[error.message];
        if (!errorMessage) {
          errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
        }

        throw new Error(errorMessage);
      });
  }

  public static async loadFeedbacks(ids: number[]): Promise<any> {
    return EnrouteApiClient.post(
      `/batch`, 
      ids?.map(id => {
        return {
          method: "GET", 
          url: `/task/${id}/feedback`
        }
      })
    ).then(resp => mapAxiosResponse<any>(resp))
    .catch(error => {
      let errorMessage = ServerErrorMessages[error.message];
      if (!errorMessage) {
        errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
      }

      throw new Error(errorMessage);
    });
  }

  public static async getSessionInfoForTasks(taskIDs: number[]): Promise<any | Error> {
    return EnrouteApiClient.post(
      `/batch`, 
      taskIDs?.map(id => {
        return {
          method: "GET", 
          url: `/task/${id}/session`
        }
      })
    ).then(resp => mapAxiosResponse<any>(resp))
    .catch(error => {
      let errorMessage = ServerErrorMessages[error.message];
      if (!errorMessage) {
        errorMessage = ServerErrorMessages[ServerError.UnexpectedError];
      }

      throw new Error(errorMessage);
    });
  }

}
